import type {ReactNode} from 'react';
import React from 'react';
import {classes, style} from './doppe-viewer-form-field.st.css';
import type {ReactBasicCompositionProps} from '@wix/devzai-utils-react';
import {reactCreateMemo, useSsrSafeHtmlId} from '@wix/devzai-utils-react';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import type {Evaluable, Values} from '@wix/devzai-utils-common';
import {evaluateWhenFunction} from '@wix/devzai-utils-common';

export const DoppeViewerFormFieldLayout = {
    LabelInlineWithInput: 'labelInline',
    LabelAboveInput: 'labelAboveInput'
} as const;

export type DoppeViewerFormFieldLayout = Values<typeof DoppeViewerFormFieldLayout>;

export const DoppeViewerFormField = reactCreateMemo(function DoppeViewerFormField(
    props: DoppeViewerFormField.Props
) {
    const {
        label,
        children,
        onInputBlur,
        labelClassName,
        inputError,
        layout = DoppeViewerFormFieldLayout.LabelAboveInput,

        className,
        ...htmlAttributes
    } = props;

    const htmlId = useSsrSafeHtmlId();
    const inputReactNode = evaluateWhenFunction(children, {htmlId: htmlId});
    const evaluatedLabel = evaluateWhenFunction(label, {htmlId: htmlId});

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {layout: layout}, className)}
        >
            {evaluatedLabel ? (
                <div
                    className={style(classes.blockLabelContent, FlexLayout.row.alignToCenter)}
                >
                    <label
                        htmlFor={htmlId}
                        className={style(classes.label, {}, labelClassName, CommonClasses.ellipsis)}
                    >
                        {evaluatedLabel}
                    </label>
                </div>
            ) : null}
            {
                inputReactNode ?
                    <div
                        onBlur={onInputBlur}
                        className={style(classes.labelAboveInputLayoutInputContainer, {})}>
                        {inputReactNode}
                    </div>
                    :
                    null
            }

            {inputError !== undefined ? (
                <div
                    className={style(classes.inputErrorMessage, {})}
                >
                    {inputError}
                </div>
            ) : null}
        </div>
    );
});


export namespace DoppeViewerFormField {

    export interface PartRenderProps {
        htmlId: string;
    }

    export interface Props extends ReactBasicCompositionProps.WithCustomChildren {
        label?: Evaluable<(renderProps: PartRenderProps) => ReactNode>;
        children: Evaluable<(renderProps: PartRenderProps) => ReactNode>;
        onInputBlur?: React.HTMLAttributes<any>['onBlur'];
        labelClassName?: string;
        inputError?: string;
        layout?: DoppeViewerFormFieldLayout;
    }
}

